import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import styles from "./Product.module.css";
import API_BASE_URL from "../../config/api";

function ReviewForm({ productId, productName, onClose }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    rating: 0,
    image: null,
  });

  // message and error message state
  const [messageVisible, setMessageVisible] = useState(false); // success message visibility
  const [errors, setErrors] = useState({});

  // pop up ref
  const popupRef = useRef();

  // handle close state
  const [isClosing, setIsClosing] = useState(false);
  const handleClose = () => {
    setIsClosing(true); // Start the closing animation
    setTimeout(() => {
      onClose(); // Remove the modal from the DOM after the animation
      setIsClosing(false); // Reset the state
    }, 300); // Match the animation duration (300ms)
  };

  // close the modal if user click outside the form
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [handleClose]);

  // validat form
  const validateForm = () => {
    const errors = {};
    if (!formData.title) errors.title = "Title is required.";
    if (!formData.description) errors.description = "Description is required.";
    if (!formData.rating) errors.rating = "Please select a rating.";
    if (
      formData.image &&
      !["image/png", "image/jpg", "image/jpeg"].includes(formData.image.type)
    ) {
      errors.image = "Only PNG, JPG, or JPEG files are allowed.";
    }
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, image: file });
  };

  // Handle star rating click
  const handleStarClick = (rating) => {
    setFormData({ ...formData, rating });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const formDataToSend = new FormData();
    formDataToSend.append("product_id", productId); // use the prop
    formDataToSend.append("product_name", productName); // use the prop
    formDataToSend.append("title", formData.title);
    formDataToSend.append("description", formData.description);
    formDataToSend.append("rating", formData.rating);
    if (formData.image) {
      formDataToSend.append("image", formData.image);
    }

    try {
      await axios.post(`${API_BASE_URL}/handleReviewSubmission.php`, formDataToSend,
        {
          // change to php
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setMessageVisible(true); // show success message
      setFormData({ title: "", description: "", rating: 0, image: null });
      setErrors({});

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "Failed to submit review.";
      alert(errorMsg);
      console.error("Error submitting review:", error);
    }
  };

  return (
    <div
      className={`${styles.modalOverlay} ${
        isClosing ? styles.fadeOut : styles.fadeIn
      }`}
    >
      <div
        className={`${styles.review_form_container} ${
          isClosing ? styles.scaleDown : styles.scaleUp
        }`}
        ref={popupRef}
      >
        <h1>Leave a Review</h1>
        {/* {message && <p>{message}</p>} */}
        <form className={styles.review_form} onSubmit={handleSubmit}>
          <div className={styles.review_inputs_div}>
            <div className={styles.review_inner_input}>
              <span>Title</span>
              <input
                name="title"
                value={formData.title}
                onChange={handleChange}
              />
              {errors.title && (
                <span className={styles.error}>{errors.title}</span>
              )}
            </div>
            <div className={styles.review_inner_input}>
              <span>Description</span>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
              {errors.description && (
                <span className={styles.error}>{errors.description}</span>
              )}
            </div>
            <div className={styles.review_inner_input}>
              <span>Rating</span>
              <div className={styles.star_rating}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={`${styles.star} ${
                      formData.rating >= star ? styles.selected : ""
                    }`}
                    onClick={() => handleStarClick(star)}
                  >
                    ★
                  </span>
                ))}
              </div>
              {errors.rating && (
                <span className={styles.error}>{errors.rating}</span>
              )}
            </div>
            <div className={styles.review_inner_input}>
              <span>Image</span>
              <input type="file" onChange={handleFileChange} />
              {errors.image && (
                <span className={styles.error}>{errors.image}</span>
              )}
            </div>
          </div>
          <div className={styles.submit_btn}>
            <button type="submit">Submit Review</button>
          </div>
        </form>
        <button
          onClick={handleClose}
          className={styles.close_button}
          aria-label="Close Review Form"
        >
          &times;
        </button>
      </div>

      {/* Success message modal */}
      {messageVisible && (
        <div className={styles.success_modal}>
          <p>Review submitted successfully!</p>
          <div className={styles.loading_message}>
            <div className={styles.spinner}></div>
            <p>Refreshing the page...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReviewForm;
